import { u340xIsValidYm, u340xShiftYm } from './u340xKpiYm';

/**
 * Profondeur max de l’ancre (fin) depuis aujourd’hui : 5 ans (60 mois).
 * L’usager peut terminer la fenêtre n’importe où dans `[today−60, today]`.
 */
export const U340X_KPI_TIMELINE_PAST_MONTHS = 60;

/** Intervalle des ticks / labels sur la timeline (tous les 3 mois). */
export const U340X_KPI_TIMELINE_TICK_EVERY = 3;

/**
 * Construit la piste ordonnée pour afficher une fenêtre de `windowMonths` mois
 * se terminant n’importe où dans `[today−60, today]` (5 ans).
 *
 * Début piste = today − 60 − (windowMonths − 1)  (ex. N=12 → today−71)
 * Fin piste   = today
 *
 * Ainsi la bande de N mois tient toujours entièrement sur la piste.
 */
export function u340xBuildTimelineMonths(
  todayYm: string,
  windowMonths = 1,
): string[] {
  if (!u340xIsValidYm(todayYm)) return [];
  const count = Math.max(1, Math.floor(windowMonths) || 1);
  const pastFromToday = U340X_KPI_TIMELINE_PAST_MONTHS + (count - 1);
  const months: string[] = [];
  for (let offset = -pastFromToday; offset <= 0; offset += 1) {
    const ym = u340xShiftYm(todayYm, offset);
    if (ym) months.push(ym);
  }
  return months;
}

/** Tick / label visible tous les `U340X_KPI_TIMELINE_TICK_EVERY` mois (index 0-based). */
export function u340xIsTimelineTick(index: number): boolean {
  return index >= 0 && index % U340X_KPI_TIMELINE_TICK_EVERY === 0;
}

/**
 * Borne un mois dans `[todayYm − 60, todayYm]` (plage d’ancrage 5 ans).
 * Retourne null si l’un des arguments est invalide.
 */
export function u340xClampTimelineYm(ym: string, todayYm: string): string | null {
  if (!u340xIsValidYm(ym) || !u340xIsValidYm(todayYm)) return null;
  const minYm = u340xShiftYm(todayYm, -U340X_KPI_TIMELINE_PAST_MONTHS);
  if (!minYm) return null;
  if (ym < minYm) return minYm;
  if (ym > todayYm) return todayYm;
  return ym;
}

/**
 * Mois d’ancrage (fin) le plus ancien : toujours `today − 60` (5 ans),
 * indépendant de `windowMonths` (la piste est allongée pour contenir la fenêtre).
 */
export function u340xMinSelectableAnchorYm(todayYm: string): string | null {
  if (!u340xIsValidYm(todayYm)) return null;
  return u340xShiftYm(todayYm, -U340X_KPI_TIMELINE_PAST_MONTHS);
}

/**
 * Borne l’ancre (fin de fenêtre) dans `[today − 60, today]`.
 * `windowMonths` est accepté pour compat API mais n’influence plus le plancher.
 */
export function u340xClampTimelineAnchorYm(
  ym: string,
  todayYm: string,
  _windowMonths?: number,
): string | null {
  return u340xClampTimelineYm(ym, todayYm);
}

export interface U340xKpiTimelineWindowRange {
  startYm: string;
  endYm: string;
}

/**
 * Fenêtre de série : `windowMonths` mois se terminant à `anchorYm` (inclus).
 * Ex. anchor=2025-10, windowMonths=12 → start=2024-11, end=2025-10.
 */
export function u340xTimelineWindowRange(
  anchorYm: string,
  windowMonths: number,
): U340xKpiTimelineWindowRange | null {
  if (!u340xIsValidYm(anchorYm) || !Number.isFinite(windowMonths) || windowMonths < 1) {
    return null;
  }
  const count = Math.floor(windowMonths);
  const startYm = u340xShiftYm(anchorYm, -(count - 1));
  if (!startYm) return null;
  return { startYm, endYm: anchorYm };
}

/**
 * Interprète un clic sur la timeline :
 * - mois **à gauche** de la fenêtre courante → devient le **début**, fin = début+(N−1)
 * - sinon (dans la fenêtre ou à droite) → devient la **fin** (comportement actuel)
 */
export function u340xResolveAnchorFromClick(params: {
  clickedYm: string;
  windowMonths: number;
  todayYm: string;
  currentStartYm: string | null | undefined;
}): string | null {
  const { clickedYm, windowMonths, todayYm, currentStartYm } = params;
  if (!u340xIsValidYm(clickedYm) || !Number.isFinite(windowMonths) || windowMonths < 1) {
    return null;
  }
  const count = Math.floor(windowMonths);

  if (currentStartYm && u340xIsValidYm(currentStartYm) && clickedYm < currentStartYm) {
    const endFromStart = u340xShiftYm(clickedYm, count - 1);
    if (!endFromStart) return null;
    return u340xClampTimelineAnchorYm(endFromStart, todayYm);
  }

  return u340xClampTimelineAnchorYm(clickedYm, todayYm);
}
